import type { EngineStatus, Plan, Role, UserStatus } from "@gf/shared";

const nf = new Intl.NumberFormat("en-US");
const compact = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });

export function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return nf.format(n);
}

/** 12,345 → "12.3K" (exact below 10,000). */
export function formatCount(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return Math.abs(n) < 10_000 ? nf.format(n) : compact.format(n);
}

export function formatDate(ms: number | null | undefined, opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" }): string {
  if (!ms) return "—";
  return new Intl.DateTimeFormat(undefined, opts).format(new Date(ms));
}

export function formatTime(ms: number | null | undefined): string {
  if (!ms) return "—";
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(new Date(ms));
}

export function formatDateTime(ms: number | null | undefined): string {
  if (!ms) return "—";
  const d = new Date(ms);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) return `today at ${formatTime(ms)}`;
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (d.toDateString() === tomorrow.toDateString()) return `tomorrow at ${formatTime(ms)}`;
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(d);
}

const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

/** "just now", "5 min ago", "in 3 hours", "yesterday"… */
export function relativeTime(ms: number | null | undefined, now = Date.now()): string {
  if (!ms) return "never";
  const diff = ms - now;
  const abs = Math.abs(diff);
  if (abs < 45_000) return "just now";
  const min = 60_000;
  const hour = 60 * min;
  const day = 24 * hour;
  if (abs < hour) return rtf.format(Math.round(diff / min), "minute").replace("minutes", "min").replace("minute", "min");
  if (abs < day) return rtf.format(Math.round(diff / hour), "hour");
  if (abs < 7 * day) return rtf.format(Math.round(diff / day), "day");
  return formatDate(ms);
}

export const PLAN_LABELS: Record<Plan, string> = {
  trial: "Trial",
  starter: "Starter",
  growth: "Growth",
  pro: "Pro",
  lifetime: "Lifetime",
  agency: "Agency",
};

export const ROLE_LABELS: Record<Role, string> = {
  admin: "Admin",
  reseller: "Reseller",
  user: "Customer",
};

export const STATUS_LABELS: Record<UserStatus, string> = {
  active: "Active",
  suspended: "Suspended",
};

export function planLabel(p: string | null | undefined): string {
  return (p && PLAN_LABELS[p as Plan]) || (p ? p[0]!.toUpperCase() + p.slice(1) : "—");
}

export type EngineState = "running" | "paused" | "idle" | "unknown";

/** Collapse EngineStatus into one display state. */
export function engineState(s: EngineStatus | null | undefined, now = Date.now()): EngineState {
  if (!s || s.updatedAt === null) return "unknown";
  if (s.pausedUntil && s.pausedUntil > now) return "paused";
  if (!s.running && s.pausedUntil === null && s.pauseReason) return "paused";
  return s.running ? "running" : "idle";
}

export function firstName(name: string | null | undefined): string {
  const n = (name ?? "").trim().split(/\s+/)[0];
  return n || "boss";
}

export function greeting(d = new Date()): string {
  const h = d.getHours();
  if (h < 5) return "Burning the midnight oil";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}
